import {
  AddressLocationType,
  CommonFieldProps
} from '../../../../../components'
import { useFieldProps, useLocalizedOptionsValue } from '../../../../../hooks'
import { getSelectOptionDisplayValue, MaybeString } from '../../../../../utils'
import { BaseDetailsAddress } from '../types'
import { isAddressLocationDomestic } from '../utils'

export enum AddressUpdateEventType {
  BLUR = 'blur',
  CHANGE = 'change'
}

export enum AddressUpdateFieldType {
  ADDRESSLOCATION = 'addressLocation',
  COUNTRYCODE = 'countryCode',
  POSTALAREA = 'postalArea',
  POSTALNUMBER = 'postalNumber'
}

export interface CountryUpdateType {
  fieldType: AddressUpdateFieldType.COUNTRYCODE
  selectedValue: string
}

export interface OtherUpdateType {
  fieldType:
    | AddressUpdateFieldType.ADDRESSLOCATION
    | AddressUpdateFieldType.POSTALAREA
    | AddressUpdateFieldType.POSTALNUMBER
  eventType: AddressUpdateEventType
  event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
}

type AddressUpdateProps = CountryUpdateType | OtherUpdateType

export const useAddressUpdateFn = (
  name: string,
  executeOnChangeFn?: CommonFieldProps['executeOnChangeFn']
) => {
  const {
    onBlur: onAddressBlur,
    onChange: onAddressChange,
    setValue: setAddressValue,
    value: addressValue
  } = useFieldProps<BaseDetailsAddress>(name, false)

  const postalCodeOptions = useLocalizedOptionsValue('postalCodeOptions')

  const countryOptions = useLocalizedOptionsValue('countryOptions')

  const getPostalArea = (newPostalNumberValue: MaybeString) =>
    getSelectOptionDisplayValue(postalCodeOptions, newPostalNumberValue)

  const getCountry = (newCountryCodeValue: MaybeString) =>
    getSelectOptionDisplayValue(countryOptions, newCountryCodeValue)

  const getCountryProps = (newValue: MaybeString) => ({
    updatedCountryCode: newValue || '',
    updatedCountry: getCountry(newValue)
  })

  const getUpdatedOrAddressValue = (
    updatedValue: MaybeString,
    addressKey: keyof BaseDetailsAddress
  ) => {
    return updatedValue != null ? updatedValue : addressValue[addressKey]
  }

  const updateAddress = ({ ...props }: AddressUpdateProps) => {
    const { addressLocation, postinumero } = addressValue

    let updatedCountryProps
    let updatedPostalNumberValue
    let updatedPostalAreaValue

    if (props.fieldType === AddressUpdateFieldType.POSTALNUMBER) {
      updatedPostalNumberValue = props.event.target.value
      const isDomesticAddress = isAddressLocationDomestic(addressLocation)
      if (isDomesticAddress && updatedPostalNumberValue.length === 5)
        updatedPostalAreaValue = getPostalArea(updatedPostalNumberValue)
    } else if (props.fieldType === AddressUpdateFieldType.POSTALAREA) {
      updatedPostalAreaValue = props.event.target.value
    } else if (props.fieldType === AddressUpdateFieldType.ADDRESSLOCATION) {
      const newAddressLocationValue = props.event.target.value
      if (isAddressLocationDomestic(newAddressLocationValue)) {
        updatedCountryProps = getCountryProps(AddressLocationType.DOMESTIC)
        updatedPostalAreaValue = getPostalArea(postinumero)
      }
    } else if (props.fieldType === AddressUpdateFieldType.COUNTRYCODE) {
      updatedCountryProps = getCountryProps(props.selectedValue)
    }

    const updatedAddress = {
      ...addressValue,
      maa: getUpdatedOrAddressValue(updatedCountryProps?.updatedCountry, 'maa'),
      maaKoodi: getUpdatedOrAddressValue(
        updatedCountryProps?.updatedCountryCode,
        'maaKoodi'
      ),
      postinumero: getUpdatedOrAddressValue(
        updatedPostalNumberValue,
        'postinumero'
      ),
      postitoimipaikka: getUpdatedOrAddressValue(
        updatedPostalAreaValue,
        'postitoimipaikka'
      )
    }

    setAddressValue(updatedAddress, true)

    if (props.fieldType !== AddressUpdateFieldType.COUNTRYCODE) {
      const event = props.event
      if (props.eventType === AddressUpdateEventType.CHANGE) {
        onAddressChange(event)
        executeOnChangeFn?.()
      } else {
        onAddressBlur(event)
      }
    }
  }

  return updateAddress
}
